import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import AppBreadcrumbs from 'components/AppBreadcrumbs';
import PageLayout from 'layouts/PageLayout';
import { makeStyles } from '@material-ui/core/styles';
import { useAsyncFn } from 'react-use';
import { getProvisionedJobRole, updateProvisionedJobRole } from 'http/hyperAdminProvisioned';
import { useRouteMatch, useHistory, useLocation } from 'react-router-dom';
import NotFound from 'pages/NotFound';
import { routesAliases } from 'router/routesAliases';
import { useTranslation } from 'react-i18next';
import TaskForm from 'pages/Tasks/Form';
import JobTaskVersionsTable from 'pages/Tasks/Edit/JobTaskVersionsTable';

const useStyles = makeStyles(theme => ({
  pageContent: {
    padding: theme.spacing(4, 5),
  },
}));

const persistReturnTab = returnTab => {
  if (returnTab != null) {
    localStorage.setItem('companyEditActiveTab', String(returnTab));
  }
};

const EditRole = ({ routeProps }) => {
  const routerMatch = useRouteMatch();
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const { companyId, roleId } = routerMatch.params;
  const numericCompanyId = Number(companyId);
  const [notFoundError, setNotFoundError] = useState(false);
  const [initialFormState, setInitialFormState] = useState(null);
  const [isFromSystem, setIsFromSystem] = useState(false);
  const backUrl = `${routesAliases.editCompany}/${companyId}`;
  const returnTab = location.state?.returnTab;

  const classes = useStyles();
  const breadCrumbsItems = [
    {
      title: t(routeProps.parentTitle),
      link: routeProps.parentPath,
    },
    {
      title: t('routes.editCompany'),
      link: backUrl,
    },
    {
      title: t(routeProps.title),
      isActive: true,
    },
  ];

  const [submittingState, sendForm] = useAsyncFn(async formData => {
    const updateResult = await updateProvisionedJobRole(numericCompanyId, roleId, formData);
    if (!updateResult.error && !updateResult.statusCode) {
      persistReturnTab(returnTab);
      history.push(backUrl);
    }
    return updateResult;
  }, [backUrl, history, numericCompanyId, returnTab, roleId]);

  const [fetchJobTaskState, fetchJobTaskData] = useAsyncFn(async id => {
    const jobTask = await getProvisionedJobRole(numericCompanyId, id);
    if (!jobTask.error) {
      setInitialFormState({
        name: jobTask.name,
      });
      setIsFromSystem(Boolean(jobTask.isFromSystem));
    } else if (jobTask.statusCode === 404) {
      setNotFoundError(true);
    }
    return jobTask;
  }, [numericCompanyId]);

  useEffect(() => {
    setNotFoundError(false);
    setInitialFormState(null);
    fetchJobTaskData(roleId);
  }, [roleId]);

  return notFoundError ? (
    <NotFound />
  ) : (
    <PageLayout
      pageTitle={<AppBreadcrumbs items={breadCrumbsItems} />}
      classes={{ content: classes.pageContent }}
      showLoader={fetchJobTaskState.loading}
    >
      <TaskForm
        key={roleId}
        onSubmitForm={sendForm}
        submitting={submittingState.loading}
        formType="edit"
        initialValues={initialFormState}
        backUrl={backUrl}
        isReadOnly={false}
        onCancel={() => {
          persistReturnTab(returnTab);
          history.push(backUrl);
        }}
      />
      <JobTaskVersionsTable
        jobTaskId={roleId}
        isFromSystem={isFromSystem}
        provisionedCompanyId={numericCompanyId}
      />
    </PageLayout>
  );
};

EditRole.propTypes = {
  routeProps: PropTypes.shape({
    parentPath: PropTypes.string,
    parentTitle: PropTypes.string,
    title: PropTypes.string,
  }).isRequired,
};

export default EditRole;
